import {Injectable} from "@angular/core";
import {HttpClient, HttpContext, HttpParams} from "@angular/common/http";
import {BehaviorSubject, Observable, of} from "rxjs";
import {catchError, filter, switchMap, take, tap, timeout} from "rxjs/operators";

import {AuthService} from "~core/services/auth.service";
import {SKIP_NOTIFICATION} from "~core/interceptors/error-handler.interceptor";

import {environment} from "../../../environments/environment";
import {IItemPrices, IOrderLines, LeadTime} from "../../shared/types";


/** Shown wherever a price or the order action is hidden because Odoo is unreachable. */
export const PRICE_UNAVAILABLE_MESSAGE = 'Prijzen even niet beschikbaar, contacteer ons indien het probleem aanhoudt.';

/**
 * Shown wherever a price is hidden because a herberekening never reported back. That is a
 * fault rather than a wait, and it puts every price in the project in doubt, not only the
 * one whose change triggered it. Worded for clients and shown to staff unchanged, so that
 * what a client is looking at is never a guess.
 */
export const PRICE_UNCONFIRMED_MESSAGE = 'Er liep iets mis bij de prijsberekening, waardoor geen enkele prijs in dit project betrouwbaar is. Contacteer ons zodat we kunnen nakijken wat er misloopt.';

/**
 * Shown on one price that Odoo priced and reported as wrong. Says what is wrong with *this*
 * price and, just as importantly, which numbers on screen the reader can still rely on — a
 * client who reads it as "all prices are off" would distrust prices that are correct. It
 * points at the question marks rather than at "the others", because a defect inside an item
 * marks its group and the project total too: everything it touches is already flagged.
 */
export const PRICE_INVALID_MESSAGE = 'Deze prijs kunnen we niet correct berekenen en tonen we daarom niet. De prijzen zonder vraagteken kloppen wel. Contacteer ons zodat we dit kunnen nakijken.';

/**
 * Shown on the project total when a price feeding into it is missing or wrong. The total is
 * withheld rather than shown short: a lower number that looks complete is worse than none.
 */
export const PRICE_INCOMPLETE_MESSAGE = 'Het projecttotaal is onvolledig omdat één of meer prijzen niet correct berekend kunnen worden. Contacteer ons zodat we dit kunnen nakijken.';

/** Hard cap so a hanging Odoo request never keeps prices on the critical path; on timeout we treat them as unavailable. */
const ODOO_TIMEOUT_MS = 15000;

@Injectable({ providedIn: 'root' })
export class OrderLineService {
	private odooUrl: string = environment.odooUrl;
	private orderLinesUrl: string | undefined = this.odooUrl.length ? new URL('orderlines', this.odooUrl).href : undefined;
	private silentContext = { context: new HttpContext().set(SKIP_NOTIFICATION, true) };

	private _priceServiceReachable$ = new BehaviorSubject<boolean>(true);
	/** Emits false once an Odoo price request fails or times out, true again once one succeeds. */
	public priceServiceReachable$ = this._priceServiceReachable$.asObservable();

	constructor(
		private http: HttpClient,
		private authService: AuthService,
	) {}

	/** True when an Odoo backend URL is configured, i.e. price data can be fetched. */
	public get isPriceDataAvailable(): boolean {
		return this.odooUrl.length > 0;
	}

	private withAuth<T>(operation: Observable<T>): Observable<T> {
		return this.authService.currentUser$.pipe(
			filter((user) => !!user),
			take(1),
			switchMap(() => operation)
		);
	}

	private checkOdooUrl<T>(operation: Observable<T>, fallback: T): Observable<T> {
		if (!this.odooUrl.length) {
			console.warn("Odoo URL is empty, no backend calls will be made");
			return of(fallback);
		}
		return this.withAuth(operation).pipe(
			timeout(ODOO_TIMEOUT_MS),
			tap(() => this._priceServiceReachable$.next(true)),
			catchError((error) => {
				console.error('Odoo request failed', error);
				this._priceServiceReachable$.next(false);
				return of(fallback);
			})
		);
	}

	getProjectOrderLines(projectId?: string): Observable<IOrderLines> {
		const httpCall = this.http.get<IOrderLines>(`${this.orderLinesUrl!}/${projectId}`, this.silentContext);
		return this.checkOdooUrl(httpCall, null);
	}

	getAccountOrderLines(accountId: string): Observable<IOrderLines[]> {
		const url = this.odooUrl.length ? new URL(`account/${accountId}/orderlines`, this.odooUrl).href : undefined;
		const httpCall = this.http.get<IOrderLines[]>(url!, this.silentContext);
		return this.checkOdooUrl(httpCall, []);
	}

	getProjectItemPrices(projectId: string, categories: string[] = ['ITEM', 'PART', 'CUTTINGLISTLINE']): Observable<IItemPrices> {
		const url = this.odooUrl.length ? new URL(`orderlines/${projectId}/prices`, this.odooUrl).href : '';
		let params = new HttpParams();
		categories.forEach(c => params = params.append('categories', c));
		const httpCall = this.http.get<IItemPrices>(url, { ...this.silentContext, params });
		return this.checkOdooUrl(httpCall, null);
	}

	getLeadTime(): Observable<LeadTime> {
		const url = this.odooUrl.length ? new URL('leadtime', this.odooUrl).href : '';
		const httpCall = this.http.get<LeadTime>(url, this.silentContext);
		return this.checkOdooUrl(httpCall, null);
	}
}
